import { GameLayer } from "../../../../lib/gamemap/GameLayer";
import { ProcessedCacheTypes } from "./ProcessedCacheTypes";
import { ewent } from "../../../../lib/reactive";
import { MapEntity } from "../../../../lib/gamemap/MapEntity";
import { Rectangle } from "../../../../lib/math";
import { TileArea } from "../../../../lib/runescape/coordinates/TileArea";
import { PrototypeInstanceProperties, PrototypeProperties } from "./PrototypeExplorer";
import { FloorLevels, ZoomLevels } from "../../../../lib/gamemap/ZoomLevels";
import * as leaflet from "leaflet";
var PrototypeInstance = ProcessedCacheTypes.PrototypeInstance;
import { LeafletUtils } from "../../../../lib/gamemap/LeafletUtils";
import { LeafletPolygonConstructors } from "../../../../lib/gamemap/LeafletPolygonConstructors";
var areaPolygon = LeafletPolygonConstructors.areaPolygon;
export class PrototypeFilter {
    cached() {
        const self = this;
        return new class extends PrototypeFilter {
            constructor() {
                super(...arguments);
                this.lookup_table = {
                    loc: [],
                    npc: []
                };
            }
            applyInstance(instance) {
                return self.applyInstance(instance);
            }
            applyPrototype(prototype) {
                if (this.lookup_table[prototype.id[0]][prototype.id[1]] == undefined) {
                    return this.lookup_table[prototype.id[0]][prototype.id[1]] = self.applyPrototype(prototype);
                }
                return this.lookup_table[prototype.id[0]][prototype.id[1]];
            }
        };
    }
}
(function (PrototypeFilter) {
    function none() {
        return new class extends PrototypeFilter {
            applyPrototype(prototype) {
                return true;
            }
            applyInstance(instance) {
                return true;
            }
        };
    }
    PrototypeFilter.none = none;
    function forConfig(config) {
        return new class extends PrototypeFilter {
            applyInstance(instance) {
                return true;
            }
            applyPrototype(prototype) {
                if (config.names?.length > 0 && !config.names.some(n => prototype.name.toLowerCase().includes(n)))
                    return false;
                if (config.action_names?.length > 0 && !config.action_names.some(n => prototype.actions.some(a => a[0].toLowerCase().includes(n))))
                    return false;
                if (config.type && config.type != prototype.id[0])
                    return false;
                return true;
            }
        };
    }
    PrototypeFilter.forConfig = forConfig;
    PrototypeFilter.pre_filter = forConfig({
        action_names: ["open", "use", "enter", "climb", "crawl", "scale", "pass", "jump", "leave", "teleport", "descend", "step", "walk", "cross", "exit", "squeeze",
            "stand", "ascend", "top", "bottom", "descend", "across", "swing", "slash", "pray", "operate", "pull", "dig", "push", "grapple",
            "board", "swim", "through", "past", "attune", "traverse", "vault", "slide", "merge", "activate", "charge", "chop-down"],
        names: [],
        type: undefined
    }).cached();
})(PrototypeFilter || (PrototypeFilter = {}));
export class PrototypeInstanceDataSource {
    constructor(data) {
        this.data = data;
        this.created = ewent();
        this.removed = ewent();
    }
    get() {
        return this.data;
    }
    static fromList(instance) {
        return new PrototypeInstanceDataSource(instance);
    }
}
(function (PrototypeInstanceDataSource) {
    class Mutable extends PrototypeInstanceDataSource {
        constructor(index, start_data) {
            super([]);
            this.index = index;
            this.data = start_data.flatMap(instance => {
                const prototype = index.lookup(instance.id);
                if (!prototype)
                    return [];
                return [new Mutable.MutableInstance(prototype, instance, this)];
            });
        }
        create(instance) {
            const prototype = this.index.lookup(instance.id);
            if (prototype) {
                const i = new Mutable.MutableInstance(prototype, instance, this);
                this.data.push(i);
                this.created.trigger(i);
            }
        }
        remove(instance) {
            const i = this.data.indexOf(instance);
            if (i >= 0) {
                this.data.splice(i, 1);
                this.removed.trigger(instance);
            }
        }
    }
    PrototypeInstanceDataSource.Mutable = Mutable;
    (function (Mutable) {
        class MutableInstance extends PrototypeInstance {
            constructor(prototype, instance, source) {
                super(prototype, instance);
                this.source = source;
            }
            deleteInstance() {
                this.source.remove(this);
            }
        }
        Mutable.MutableInstance = MutableInstance;
    })(Mutable = PrototypeInstanceDataSource.Mutable || (PrototypeInstanceDataSource.Mutable = {}));
})(PrototypeInstanceDataSource || (PrototypeInstanceDataSource = {}));
export class PrototypeInstanceEntity extends MapEntity {
    constructor(instance, parsing_table) {
        super();
        this.instance = instance;
        this.parsing_table = parsing_table;
        this.zoom_sensitivity_layers = new ZoomLevels([
            { min: -100, hidden_here: true, value: { scale: 0.25 } },
            { min: 2, value: { scale: 0.5 } },
            { min: 5, value: { scale: 1 } },
        ]);
        this.floor_sensitivity_layers = FloorLevels.single(instance.box.origin.level);
        this.setTooltip(() => new PrototypeInstanceProperties(this.instance));
    }
    async render_implementation(props) {
        const parsing_group = this.parsing_table && this.parsing_table.getPairing(this.instance);
        const has_parser = this.rendered_with_parser = !!parsing_group?.group;
        const has_instance_parser = has_parser && (!parsing_group.group.parser.instance_group_required || parsing_group.instance_group);
        const type_color = this.instance.isLoc() ? "cyan" : "yellow";
        const parser_color = has_parser ? "green" : "orange";
        const box = areaPolygon(this.instance.box).setStyle({
            fillColor: this.parsing_table ? parser_color : type_color,
            color: this.parsing_table ? parser_color : type_color,
            stroke: true
        }).addTo(this);
        let true_west;
        const rect = Rectangle.extend(TileArea.toRect(this.instance.box), 0.5);
        switch (this.instance.instance.rotation) {
            case 0:
                true_west = [Rectangle.bottomLeft(rect), Rectangle.topLeft(rect)];
                break;
            case 1:
                true_west = [Rectangle.topLeft(rect), Rectangle.topRight(rect)];
                break;
            case 2:
                true_west = [Rectangle.topRight(rect), Rectangle.bottomRight(rect)];
                break;
            case 3:
                true_west = [Rectangle.bottomRight(rect), Rectangle.bottomLeft(rect)];
                break;
        }
        leaflet.polyline(true_west.map(LeafletUtils.latLongFromVector2), {
            color: "red"
        }).addTo(this);
        return box.getElement();
    }
    bounds() {
        return TileArea.toRect(this.instance.box);
    }
    async contextMenu(event) {
        return {
            type: "submenu",
            text: () => PrototypeProperties.renderName(this.instance.prototype),
            children: []
        };
    }
    checkParserRedraw() {
        if (this.rendered_props.render_at_all && (this.parsing_table && !!this.parsing_table.getPairing(this.instance).group) != this.rendered_with_parser) {
            this.render(true);
        }
    }
}
export class FilteredPrototypeLayer extends GameLayer {
    constructor(parsing_table) {
        super();
        this.parsing_table = parsing_table;
        this.filter = PrototypeFilter.none();
        this.lookup_table = {
            loc: [],
            npc: []
        };
        this.parsing_table.version.subscribe(() => {
            this.entity_quadtree.forEachVisible(e => {
                if (e instanceof PrototypeInstanceEntity) {
                    e.checkParserRedraw();
                }
            });
        });
    }
    addDataSource(...sources) {
        this.rendering.lock();
        sources.forEach(source => {
            source.get().forEach(instance => this.create(instance));
            source.created.on(i => {
                this.create(i);
            }).bindTo(this.lifetime_manager);
            source.removed.on(i => {
                this.delete(i);
            });
        });
        this.rendering.unlock();
        this.updateFilter();
        return this;
    }
    entry(prototype) {
        let entry = this.lookup_table[prototype.id[0]][prototype.id[1]];
        if (!entry) {
            entry = this.lookup_table[prototype.id[0]][prototype.id[1]] = {
                prototype: prototype,
                entities: []
            };
        }
        return entry;
    }
    delete(instance) {
        let entry = this.entry(instance.prototype);
        const i = entry.entities.findIndex(e => e.instance == instance);
        if (i >= 0) {
            const e = entry.entities[i];
            e.remove();
            entry.entities.splice(i, 1);
        }
    }
    create(instance) {
        let entry = this.entry(instance.prototype);
        entry.entities.push(new PrototypeInstanceEntity(instance, this.parsing_table).setVisible(this.filter.applyPrototype(instance.prototype) && this.filter.applyInstance(instance)).addTo(this));
    }
    updateFilter() {
        this.lookup_table.loc.forEach(entry => {
            if (!entry)
                return;
            const prototype_visible = this.filter.applyPrototype(entry.prototype);
            entry.entities.forEach(entity => {
                const visible = prototype_visible && this.filter.applyInstance(entity.instance);
                entity.setVisible(visible);
            });
        });
        this.lookup_table.npc.forEach(entry => {
            if (!entry)
                return;
            const prototype_visible = this.filter.applyPrototype(entry.prototype);
            entry.entities.forEach(entity => {
                const visible = prototype_visible && this.filter.applyInstance(entity.instance);
                entity.setVisible(visible);
            });
        });
    }
    setFilter(filter) {
        this.filter = filter;
        this.updateFilter();
        return this;
    }
}
